import { useReducer, useRef } from 'react';
import { createContainer, useContainer } from 'unstated-next';
import Cookies from 'js-cookie';
import { DecodedJwt, IAMUser } from '@sls-react/typings/services/iam/auth';
import {
  trackingIdentify,
  trackingIdentifyAnonymous,
  trackingUnidentify,
} from '@sls-react/services/analytics/identify';
import { fetchIamUser } from '@sls-react/api/auth';
import { useQuery, useQueryClient } from 'react-query';
import { KnownCookie } from '@sls-react/typings/cookies';
import { useRouter } from '@sls-react/routing';
import { useMount } from 'react-use';
import { useCustomEvent } from '@sls-react/lib/events/customEvents';
import decodeJwt from '@sls-react/utils/decodeJwt';
import { fireSignedOutEvent } from '@sls-react/services/analytics/events/auth';
import { getKnownCookie } from '@sls-react/hooks/cookies';
import { ApiError } from '@sls-react/api/ApiError';

class AuthenticationException extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthenticationException';
  }
}

interface SessionState {
  loggedIn: boolean;
  jwtUser: DecodedJwt | null;
}

function sessionReducer(state: SessionState, action: any): SessionState {
  switch (action.type) {
    case 'LOGGED_IN':
      return {
        loggedIn: true,
        jwtUser: action.payload.jwt,
      };

    case 'LOGGED_OUT':
      return {
        loggedIn: false,
        jwtUser: null,
      };

    default:
      return state;
  }
}

// function createSessionMethods(state: SessionState) {
//   return {
//     loggedIn({
//       jwt,
//       user,
//     }: {
//       jwt: DecodedJwt;
//       user: IAMUser;
//     }): SessionState {},
//     loggedOut(): SessionState {},
//   };
// }

export type SessionInitialState = {
  loggedIn: boolean;
  user?: DecodedJwt | null;
};

function useSessionContainer(
  initialState: SessionInitialState = { loggedIn: false },
) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const firedSignedOut = useRef(false);

  const [state, dispatch] = useReducer(sessionReducer, {
    jwtUser: initialState.user || null,
    loggedIn: initialState.loggedIn,
  } as SessionState);

  function logout() {
    Cookies.remove('access_token' as KnownCookie);
    Cookies.remove('refresh_token' as KnownCookie);
    Cookies.remove('sls-seller-account-page-1-form' as KnownCookie);
    localStorage?.removeItem('sls-seller-account-page-1-form' as KnownCookie);
    dispatch({ type: 'LOGGED_OUT' });
    if (!firedSignedOut.current) {
      firedSignedOut.current = true;
      fireSignedOutEvent();
      trackingUnidentify();
      queryClient.clear();
    }

    // @ts-ignore
    if (router.components?.[router.route]?.Component?.securePage) {
      router.replace('/');
    }
  }

  async function login(accessToken: string, refreshToken: string) {
    firedSignedOut.current = false;

    Cookies.set('access_token' as KnownCookie, accessToken, {
      expires: 365,
    });
    Cookies.set('refresh_token' as KnownCookie, refreshToken, {
      expires: 2000,
    });
    Cookies.set('signed_in_before' as KnownCookie, '1', {
      expires: 2000,
    });

    const decoded = decodeJwt(accessToken);

    if (!decoded) {
      throw new AuthenticationException('Login received invalid access token');
    }

    loggedIn(decoded);
  }

  const { data: user, refetch } = useQuery<
    IAMUser | undefined,
    ApiError,
    IAMUser
  >(
    ['iam-user'],
    () => {
      if (!!initialState.user || !!getKnownCookie('access_token')) {
        return fetchIamUser();
      } else {
        return undefined;
      }
    },
    {
      // @ts-ignore
      placeholderData: initialState.user,
      staleTime: 30000,
      onSuccess: user => trackingIdentify(user),
      onError: logout,
    },
  );

  async function loggedIn(jwt: DecodedJwt) {
    dispatch({ type: 'LOGGED_IN', payload: { jwt } });
    queryClient.invalidateQueries();
    refetch();
  }

  useMount(() => {
    if (!state.loggedIn) {
      trackingIdentifyAnonymous();
    }
  });

  useCustomEvent('sls_logout', logout);

  return {
    login,
    logout,
    user,
    decodedToken: state.jwtUser,
    loggedIn: state.loggedIn,
    refetchUser: refetch,
  };
}

const SessionContainer = createContainer(useSessionContainer);
export function useSession() {
  return useContainer(SessionContainer);
}
export default SessionContainer;
